import { createReducer, isAnyOf, WritableDraft } from "@reduxjs/toolkit";
import { getPageModuleIds } from "../../selectors/modules.js";
import { Module, ModulesByPageId, Page } from "../../types/index.js";
import { moveArrayElement } from "../../utils/utils.js";
import { getPages } from "../pages.js";
import {
  deleteModuleTranslationStart,
  dragModule,
  moveModuleStart,
} from "./actions.js";
import { getModules, postModule } from "./thunks.js";

export const initialState: ModulesByPageId = {};

const createModule = (
  state: WritableDraft<ModulesByPageId>,
  pageId: string | null,
  moduleId: string,
  parentId: string | null,
  next?: string,
) => {
  // If the module should not be shown
  // directly on the page, but as a nested module
  if (parentId) return;

  updateModules(state, pageId, (modules) => {
    // If no next moduleId is set, append it to the end.
    if (!next) {
      return [...modules, moduleId];
    }

    const nextIndex = modules.indexOf(next);

    if (nextIndex === -1) {
      throw new Error(`The next module (${next}) doesn’t exist.`);
    }

    // Insert before the next module
    return [
      ...modules.slice(0, nextIndex),
      moduleId,
      ...modules.slice(nextIndex),
    ];
  });
};

const dragModuleInternal = (
  state: WritableDraft<ModulesByPageId>,
  pageId: string,
  dragIndex: number,
  hoverIndex: number,
) =>
  updateModules(state, pageId, (modules) =>
    moveArrayElement(modules, dragIndex, hoverIndex),
  );

const moveModuleInternal = ({
  state,
  pageId,
  moduleId,
  moveBy,
}: {
  state: WritableDraft<ModulesByPageId>;
  pageId: string | null;
  moduleId: string;
  moveBy: number;
}) =>
  updateModules(state, pageId, (modules) => {
    const index = modules.indexOf(moduleId);
    if (index + moveBy < 0) return modules;

    let newModules = modules.slice();
    newModules.splice(index, 1);
    newModules.splice(index + moveBy, 0, moduleId);

    return newModules;
  });

const deleteModuleTranslationInternal = ({
  state,
  pageId,
  moduleId,
  deleteAllTranslations,
}: {
  state: WritableDraft<ModulesByPageId>;
  pageId: string | null;
  moduleId: string;
  deleteAllTranslations: boolean;
}) => {
  if (!deleteAllTranslations) return;

  updateModules(state, pageId, (modules) =>
    modules.filter((key) => key !== moduleId),
  );
};

const updateModules = (
  state: WritableDraft<ModulesByPageId>,
  pageId: string | null,
  callback: (moduleIds: string[]) => string[],
) => {
  const oldModuleIds = getPageModuleIds(state, pageId) || [];

  state[String(pageId)] = callback(oldModuleIds);
};

const getModulesInternal = (
  state: WritableDraft<ModulesByPageId>,
  pageId: string,
  modules: Module[],
) => {
  const fullModules = modules.filter(({ parentId }) => !parentId);

  updateModules(state, pageId, () => fullModules.map((module) => module.id));
};

const getModulesFromPages = (
  state: WritableDraft<ModulesByPageId>,
  pages: Page[],
) =>
  pages.forEach(({ modules, id: pageId }) =>
    getModulesInternal(state, pageId, modules),
  );

const reducer = createReducer(initialState, (builder) => {
  builder.addCase(postModule.pending, (state, action) => {
    const { pageId, moduleId, parentId, next } = action.meta.arg;
    createModule(state, pageId, moduleId, parentId, next);
  });

  builder.addCase(dragModule, (state, action) => {
    const { pageId, dragIndex, hoverIndex } = action.payload;
    dragModuleInternal(state, pageId, dragIndex, hoverIndex);
  });

  builder.addCase(moveModuleStart, (state, action) => {
    moveModuleInternal({ state, ...action.payload });
  });

  builder.addCase(deleteModuleTranslationStart, (state, action) => {
    deleteModuleTranslationInternal({ state, ...action.payload });
  });

  builder.addCase(getModules.fulfilled, (state, action) => {
    getModulesInternal(state, action.meta.arg.pageId, action.payload);
  });

  builder.addCase(getPages.fulfilled, (state, action) =>
    getModulesFromPages(state, action.payload),
  );

  builder.addMatcher(
    isAnyOf(getModules.pending, getModules.rejected),
    (state, action) => {
      updateModules(state, action.meta.arg.pageId, () => []);
    },
  );
});

export default reducer;
